import React from 'react';
import { FileTextIcon } from 'lucide-react';
import { CommissionModelBadge } from '../components/CommissionModelBadge';
import { ContextCard } from '../components/ContextCard';
import { PaymentTypePicker } from '../components/PaymentTypePicker';
import { SectionCard } from '../components/SectionCard';
import { standardPayPeriod } from '../data/payPeriods';
interface SpecRow {
  property: string;
  value: string;
}
function SpecTable({
  rows
}: {
  rows: SpecRow[];
}) {
  return <dl className="divide-y divide-ink-100 rounded-lg border border-ink-200 bg-white" data-id="element-242" data-mp="2ytoaeqplvmx">
      {rows.map(row => <div key={row.property} className="flex gap-6 px-4 py-2.5" data-id="element-243" data-mp="hnpue697gykw">
          <dt className="w-40 shrink-0 text-xs font-medium text-ink-500" data-id="element-244" data-mp="cod8pou2lxjw">
            {row.property}
          </dt>
          <dd className="numeric text-xs leading-5 text-ink-800" data-id="element-245" data-mp="qnv0md2nxqxh">{row.value}</dd>
        </div>)}
    </dl>;
}
function SpecBlock({
  title,
  summary,
  rows,
  children
}: {
  title: string;
  summary: string;
  rows: SpecRow[];
  children?: React.ReactNode;
}) {
  return <section className="space-y-3" data-id="element-246" data-mp="3ion2wa8vtyyc">
      <div data-id="element-247" data-mp="3ipodyepwelg2">
        <h2 className="text-base font-semibold text-ink-900" data-id="element-248" data-mp="3ipen6ox14a9b">{title}</h2>
        <p className="mt-1 max-w-2xl text-sm text-ink-500" data-id="element-249" data-mp="3nngge2o65pyi">{summary}</p>
      </div>
      {children && <div className="rounded-lg border border-dashed border-ink-300 bg-ink-50 p-5" data-id="element-250" data-mp="3sn2qw4cm4s3m">
          {children}
        </div>}
      <SpecTable rows={rows} data-id="element-251" data-mp="igzvxn6ek8i" />
    </section>;
}
export function Specs() {
  return <main className="min-h-[calc(100vh-45px)] w-full bg-ink-100 px-6 py-10" data-id="element-252" data-mp="5gs9q8dh0uqq">
      <div className="mx-auto max-w-4xl space-y-10" data-id="element-253" data-mp="a8jto024g27w">
        <header data-id="element-254" data-mp="ab60tyl3ofpl">
          <h1 className="text-2xl font-semibold text-ink-900" data-id="element-255" data-mp="f7n09oxr2ghz">Component specs</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500" data-id="element-256" data-mp="f9qzvuom8tcg">
            Specs for the four structural pieces of the Add Payment modal. Modal shell:
            720px max width, 88vh max height, 12px radius, ink-50 body, white header and
            footer, 24px horizontal padding.
          </p>
        </header>

        <SpecBlock title="Context card" summary="Read-only band that anchors the payment to its pay period. Always visible above the picker; never editable." rows={[{
        property: 'Container',
        value: 'White, 1px #dde1e8 border, 8px radius, shadow-card, 20px / 16px padding'
      }, {
        property: 'Eyebrow',
        value: '12px medium, uppercase, tracking-wide, ink-400'
      }, {
        property: 'Record name',
        value: '16px semibold ink-900, truncates on overflow'
      }, {
        property: 'Badge slot',
        value: 'Top-right, commission model badge'
      }, {
        property: 'Detail grid',
        value: '2 columns desktop / 1 mobile, 16px gap, 1px ink-100 top divider, 16px padding-top'
      }, {
        property: 'Detail item',
        value: '16px lucide icon (ink-400) + 12px medium ink-500 label + 14px medium ink-900 value + 12px ink-500 meta'
      }, {
        property: 'Numerals',
        value: 'Tabular for the date range'
      }]} data-id="element-257" data-mp="2gae3d9l1i4yp">
          <ContextCard payPeriod={standardPayPeriod} data-id="element-258" data-mp="14iy7q7itqgjm" />
        </SpecBlock>

        <SpecBlock title="Payment type picker" summary="Card radio group that gates every dynamic section. Six options, always visible so the user can switch types; changing type clears entered values." rows={[{
        property: 'Pattern',
        value: 'role="radiogroup" of role="radio" buttons, aria-checked, arrow/tab focusable'
      }, {
        property: 'Grid',
        value: '3 columns desktop / 2 columns mobile, 8px gap'
      }, {
        property: 'Card (rest)',
        value: '1px ink-200 border, white, 8px radius, 12px padding, ink-400 icon'
      }, {
        property: 'Card (hover)',
        value: 'brand-300 border, brand-50/40 fill, brand-500 icon'
      }, {
        property: 'Card (selected)',
        value: 'brand-600 border + 1px brand-600 ring, brand-50 fill, brand-800 label'
      }, {
        property: 'Focus',
        value: '4px brand-100 ring, no outline'
      }, {
        property: 'Label / caption',
        value: '14px semibold / 12px ink-500 helper on second line'
      }]} data-id="element-259" data-mp="18hxdtvgakjcn">
          <PaymentTypePicker value="payment" onChange={() => undefined} data-id="element-260" data-mp="64r38x67ohqa" />
        </SpecBlock>

        <SpecBlock title="Section header" summary="Every dynamic field group sits in a labeled card. Sections animate in (8px rise, 220ms) as the payment type reveals them, and unmount completely when irrelevant — never disabled." rows={[{
        property: 'Card',
        value: 'White, 1px ink-200 border, 8px radius, shadow-card'
      }, {
        property: 'Header',
        value: '20px / 14px padding, 1px ink-100 bottom border'
      }, {
        property: 'Icon chip',
        value: '28px square, 6px radius, ink-100 fill, 14px ink-600 lucide icon'
      }, {
        property: 'Title',
        value: '14px semibold ink-900, h3, ordered under the modal h1'
      }, {
        property: 'Description',
        value: '12px ink-500, one line explaining when the section applies'
      }, {
        property: 'Body',
        value: '20px / 16px padding, 2-column 16px gap field grid, single column under 640px'
      }, {
        property: 'Currency field',
        value: '$ prefix left, value right-aligned, tabular numerals; primary amount semibold'
      }]} data-id="element-261" data-mp="13a4p8b797cv">
          <SectionCard title="Loan & Commission Details" description="Commission figures pulled from the loan; net is calculated but editable." icon={FileTextIcon} aside={<CommissionModelBadge model="standard" data-id="element-263" data-mp="11c9uxicq2m1q" />} data-id="element-262" data-mp="1u28onr1v37cq">
            <p className="text-xs text-ink-500" data-id="element-264" data-mp="11ap2pzdvrf71">Field grid renders here.</p>
          </SectionCard>
        </SpecBlock>

        <SpecBlock title="Sticky footer" summary="Pinned to the modal bottom while the body scrolls. Left side carries live status; right side carries the escape hatch then the primary action." rows={[{
        property: 'Container',
        value: 'sticky bottom-0, white/95 + backdrop blur, 1px ink-200 top border, 24px / 14px padding'
      }, {
        property: 'Status (default)',
        value: '12px ink-500 running payment amount, tabular numerals'
      }, {
        property: 'Status (invalid)',
        value: '12px semibold red-600 with warning icon: "N fields need attention"'
      }, {
        property: 'Secondary',
        value: 'Cancel — white, 1px ink-200 border, ink-700 label; becomes "Back" on the review step'
      }, {
        property: 'Primary',
        value: 'brand-600 fill, white 14px semibold, 6px radius, hover brand-700, focus 4px brand-200 ring'
      }, {
        property: 'Primary states',
        value: 'Disabled ink-300 until a payment type is picked; spinner + "Saving…" while in flight'
      }, {
        property: 'Labels',
        value: 'Form step "Review & Save" (arrow icon) → review step "Save Payment" (check icon)'
      }]} data-id="element-265" data-mp="1b2l1lcnsj9cb">
          <div className="overflow-hidden rounded-lg border border-ink-200 bg-white" data-id="element-266" data-mp="2ngjhf82tmu9m">
            <div className="flex items-center justify-between gap-4 px-6 py-3.5" data-id="element-267" data-mp="2if3utbkzwfp4">
              <span className="numeric text-xs text-ink-500" data-id="element-268" data-mp="2qvl0izp814yc">
                Payment amount{' '}
                <span className="font-semibold text-ink-800" data-id="element-269" data-mp="2uyr1brtke3xf">$7,930.00</span>
              </span>
              <span className="flex items-center gap-2" data-id="element-270" data-mp="2gzhxlrmjp2ac">
                <span className="rounded-md border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-700" data-id="element-271" data-mp="2lx1b2pbf5b18">
                  Cancel
                </span>
                <span className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white" data-id="element-272" data-mp="39nniqfhigtap">
                  Save Payment
                </span>
              </span>
            </div>
          </div>
        </SpecBlock>
      </div>
    </main>;
}